import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TableList from '../../../../core/table/TableList';
import TableTop from '../../../../core/table/TableTop';
import TableHead from '../../../../core/table/TableHead';
import TableBody from '../../../../core/table/TableBody';
import Dialog from '../../../../core/components/Dialog';
import TablePaging from '../../../../core/table/TablePaging';
import { useMonitorStatusTableInit } from './useMonitorStatusTableInit';
import { useMonitorStatusTableState } from './useMonitorStatusTableState';
import { useMonitorStatusData } from './useMonitorStatusData';
import { useDataMonitorStore } from '../pagehooks/useDataMonitorStore';
import TableSkeleton from '../../../../core/components/TableSkeleton';
import { useMountingSkeleton } from '../../../../core/hooks/useMountingSkeleton';
import MonitorHistoryFlyOut from '../components/MonitorHistoryFlyOut';
import { MonitorItem } from '../../data/datamonitorRepo';
import datamonitorRepo from '../../data/datamonitorRepo';
import { monitorStatusDataAtom } from './monitorstatus_atoms';
import { useSetImmerAtom } from 'jotai-immer';

// Monitor types by user mode
const CE_MONITOR_TYPES = [
    'AFFILIATION.ACKNOWLEDGEMENT.RECEIVED',
    'AFFILIATION.CATALOG.RECEIVED',
    'AFFILIATION.INVOICES.RECEIVED',
    'AFFILIATION.SPLITORDER.RECEIVED',
    'AFFILIATION.SPLITTRANSACTIONS.RECEIVED',
    'COVEREDENTITY.ENCOUNTERS.RECEIVED',
    'COVEREDENTITY.EPRESCRIBE.RECEIVED',
    'COVEREDENTITY.NPI.RECEIVED',
    'COVEREDENTITY.REFNPI.RECEIVED',
    'ORDER_IN',
];

const CDC_EXTRA_TYPES = ['COVEREDENTITY.CDC.RESTOCK', 'COVEREDENTITY.CDC.RESTOCK_EXCEPTION'];

const VERISAVEONLY_MONITOR_TYPES = [
    'AFFILIATION.ACKNOWLEDGEMENT.RECEIVED',
    'AFFILIATION.CATALOG.RECEIVED',
    'AFFILIATION.INVOICED.RECEIVED',
    'AFFILIATION.SPLITORDER.RECEIVED',
    'ORDER_IN',
    'ORDER_OUT',
];

function getMonitorTypes(isCDC: boolean, isVeriSaveOnly: boolean): string[] {
    if (isVeriSaveOnly) return VERISAVEONLY_MONITOR_TYPES;
    if (isCDC) return [...CE_MONITOR_TYPES, ...CDC_EXTRA_TYPES];
    return CE_MONITOR_TYPES;
}

type MonitorStatusTableProps = {
    isCDC: boolean;
    isVeriSaveOnly: boolean;
};

const MonitorStatusTable = ({ isCDC, isVeriSaveOnly }: MonitorStatusTableProps): JSX.Element => {
    const mounting = useMountingSkeleton();
    const { facilityVal, deepLinkMonitorType } = useDataMonitorStore();
    const monitorTypes = getMonitorTypes(isCDC, isVeriSaveOnly);

    const setData = useSetImmerAtom(monitorStatusDataAtom);
    const [subscribeError, setSubscribeError] = useState(false);

    const handleSubscribeToggle = useCallback(
        async (item: any, colKey: string, newVal: boolean): Promise<boolean> => {
            const oldVal = item[colKey];
            const newStr = newVal ? 'ON' : 'OFF';

            // Optimistic update
            setData(draft => {
                const target = draft.dataItems.find((d: any) => d.Id === item.Id);
                if (target) (target as any)[colKey] = newStr;
            });

            const email = colKey === 'SubscribedEmail' ? newVal : item.SubscribedEmail === 'ON';
            const inApp = colKey === 'SubscribedInApp' ? newVal : item.SubscribedInApp === 'ON';
            try {
                await datamonitorRepo.subscribe(item.Id, email, inApp);
                return true;
            } catch {
                // Revert on failure
                setData(draft => {
                    const target = draft.dataItems.find((d: any) => d.Id === item.Id);
                    if (target) (target as any)[colKey] = oldVal;
                });
                setSubscribeError(true);
                return false;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const subscribeErrorActions = useMemo(() => [{ label: 'OK', onClick: (): void => setSubscribeError(false) }], []);

    const { init, loadMonitors } = useMonitorStatusTableInit(monitorTypes, handleSubscribeToggle);
    const { waiting, cols, sections, sort, filters, fltStats, paging, setSort, setFilter, setPaging } =
        useMonitorStatusTableState();
    const { items } = useMonitorStatusData();

    const [popup, setPopup] = useState<MonitorItem | null>(null);
    const tableRef = useRef<HTMLTableElement>(null);

    // One-shot guard so the alert deep-link MonitorType filter is applied only on the
    // first facility-driven load; later facility changes load unfiltered.
    const deepLinkConsumedRef = useRef(false);

    // Close popup and reload data when facility changes
    useEffect(() => {
        setPopup(null);
        if (!init && facilityVal && facilityVal !== 'NONE') {
            const types = getMonitorTypes(isCDC, isVeriSaveOnly);
            let preset: string | undefined;
            if (deepLinkMonitorType && !deepLinkConsumedRef.current) {
                preset = deepLinkMonitorType;
                deepLinkConsumedRef.current = true;
            }
            loadMonitors(facilityVal, types, preset);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [init, facilityVal, isCDC, isVeriSaveOnly, loadMonitors]);

    const handleRowClick = useCallback((rowItem: MonitorItem): void => {
        setPopup(rowItem);
    }, []);

    const closePopup = useCallback((): void => {
        setPopup(null);
    }, []);

    if (mounting || init || waiting) {
        return (
            <TableSkeleton
                columns={8}
                rows={10}
                sections={5}
            />
        );
    }

    return (
        <div className="monitor-status-table-wrapper">
            <TableList>
                <TableTop
                    id="monitorStatusTable"
                    fltStats={fltStats}
                />
                <table ref={tableRef}>
                    <TableHead
                        cols={cols}
                        sections={sections}
                        sort={sort}
                        filters={filters}
                        setFilter={setFilter}
                        setSort={setSort}
                    />
                    <TableBody
                        cols={cols}
                        rows={items}
                        onClick={handleRowClick}
                    />
                </table>
                <TablePaging
                    paging={paging}
                    setPaging={setPaging}
                    fltStats={fltStats}
                />
            </TableList>
            <MonitorHistoryFlyOut
                monitorId={popup?.Id ?? null}
                title={popup ? `HISTORY for CE: ${popup.CE} - ${popup.RXVEN}` : ''}
                monitorType={popup?.MonitorType ?? ''}
                onClose={closePopup}
                ignoreClickRef={tableRef as React.RefObject<HTMLElement>}
            />
            <Dialog
                open={subscribeError}
                setOpen={setSubscribeError}
                title="Subscribe Error"
                message="Unable to update alert subscription."
                actions={subscribeErrorActions}
            />
        </div>
    );
};

export default MonitorStatusTable;
